import { Children, type ReactNode } from 'react';
import { TAG } from './registry';

/**
 * A value that is already set, with the affordance to take it back.
 *
 * Not a `Button` wearing a tone. The remove control is a 13px glyph inside a pill that is
 * eleven pixels of text tall, and `.btn`'s 44px floor would turn a row of labels into a row
 * of slabs. It reaches the 24px pointer target WCAG 2.5.8 requires through a pseudo-element
 * that grows the hit area without growing the glyph — a rule keyed on `.tag button`, so the
 * button has to be the one this primitive renders and cannot be written at a call site.
 *
 * None of these accepts `className`. See the note in `Button.tsx`.
 */
export interface RemovableTagProps {
  /** The text on the chip. */
  label: ReactNode;
  /**
   * The accessible name of the remove control — "Remove label exports", not "Remove".
   * Required, and separate from the visible text, because a column of identical "Remove"
   * buttons is a list a screen-reader user cannot navigate and removing a value has no
   * undo. The primitive does not know which noun the chip stands for; the caller does.
   */
  removeLabel: string;
  disabled?: boolean;
  onRemove: () => void;
}

export function RemovableTag({
  label,
  removeLabel,
  disabled = false,
  onRemove,
}: RemovableTagProps) {
  return (
    <span className={TAG.chip}>
      {label}
      <button type="button" aria-label={removeLabel} disabled={disabled} onClick={onRemove}>
        ×
      </button>
    </span>
  );
}

/**
 * The row the chips sit in. It wraps rather than scrolls: a chip pushed past the edge is a
 * value the reader can neither see nor remove.
 *
 * `empty` EXISTS BECAUSE NOTHING AND NOT-SET LOOK THE SAME OTHERWISE. An empty `.tags` div
 * renders as literally zero pixels, so a list that inherits from the level above and one that
 * was deliberately emptied are indistinguishable — the same confusion the `OFF` badge was added
 * to `ReminderLadder` to prevent, in a place where the reader cannot even tell there is a
 * control.
 */
export function TagList({ children, empty }: { children: ReactNode; empty?: ReactNode }) {
  // `Children.count` rather than a truthiness test: `children` is usually the result of a
  // `.map`, so an empty list arrives as `[]` — which is truthy.
  const isEmpty = Children.count(children) === 0;
  return (
    <div className={isEmpty ? TAG.empty : TAG.list}>
      {isEmpty && empty !== undefined ? empty : children}
    </div>
  );
}
